import React, { FC, PropsWithChildren } from "react";
import { useCookies } from "react-cookie";
import { Navigate } from "react-router-dom";

interface RoleGuardProps {
  allowedRoles: string[];
  requireWriteAccess?: boolean;
}

/**
 * ## RoleGuard
 * Role-based access control guard that checks user permissions based on user_type
 * - O: Owner (full access to everything)
 * - A: Admin (full access except user management)
 * - S: Sales Person (view-only access, no changes)
 * - C: Customer (no POS access)
 */
const RoleGuard: FC<PropsWithChildren<RoleGuardProps>> = ({ 
  children, 
  allowedRoles, 
  requireWriteAccess = false 
}) => {
  const [cookies] = useCookies();
  const auth = cookies.auth;
  
  // If not authenticated, redirect to login
  if (!auth) {
    return <Navigate to={'/auth'} replace />;
  }
  
  const userType = auth.user_type;
  
  // Check if user type is allowed for this route
  if (!allowedRoles.includes(userType)) {
    return <Navigate to={'/dashboard'} replace />;
  }
  
  // For write operations, check if user has write permissions
  // Sales person (S) can only view, not make changes
  if (requireWriteAccess && (userType === 'S')) {
    return <Navigate to={'/dashboard'} replace />;
  }
  
  return <>{children}</>;
};

export default RoleGuard;